<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalKitchenDivision v-if="modalShowKitchenDivision" @closeModal="closeModal"
    :kitchenDivision="selectedKitchenDivision" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Kitchen Division" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Kitchen Division" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Nama</TableHeadCell>
                    <TableHeadCell>Printer IP</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allKitchenDivisions.length == 0">
                    <TableCell colspan="6" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(kitchen, index) in allKitchenDivisions" :key="kitchen.id" :index="index"
                    isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ kitchen.name }}</TableCell>
                    <TableCell>{{ kitchen.printer_ip }}</TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + kitchen.id" @click="editKitchenDivision(kitchen)"
                        class="border border-gray-400 hover:border-green-700 hover:bg-green-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 group-hover:text-green-700" />
                      </button>
                      <button :id="'delete-data-' + kitchen.id" @click="deleteKitchenDivision(kitchen)"
                        class="border border-gray-400 hover:border-red-500 hover:bg-red-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 group-hover:text-red-700" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import ModalKitchenDivision from "@/components/modals/ModalKitchenDivision.vue";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "KitchenDivisionView",
  components: {
    IndexView, PageTitle, AddButton, ModalKitchenDivision, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, Toast, ConfirmDialog, Button
  },
  data() {
    return {
      modalShowKitchenDivision: false,
      isBlur: false,
      selectedKitchenDivision: null,
      currentPage: 1,
      totalPages: 1,
    };
  },
  computed: {
    ...mapGetters("kitchen_divisions", ["allKitchenDivisions"]),
  },
  created() {
    this.fetchKitchenDivision();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchKitchenDivision({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions("kitchen_divisions", ["deleteKitchenDivisionById"]),
    async fetchKitchenDivision(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch("kitchen_divisions/fetchKitchenDivisions", params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    showModal() {
      this.modalShowKitchenDivision = true;
      this.isBlur = true;
      this.selectedKitchenDivision = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowKitchenDivision = false;
        this.isBlur = false;
        this.fetchKitchenDivision();
      }, 200);
    },
    editKitchenDivision(type) {
      this.selectedKitchenDivision = type;
      this.modalShowKitchenDivision = true;
      this.isBlur = true;
    },
    async deleteKitchenDivision(type) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus kitchen division?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteKitchenDivisionById(type.id);
            this.fetchKitchenDivision();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus kitchen division', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus kitchen division', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus kitchen division', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus kitchen division', life: 3000 });
        }
      });
    },
  },
};
</script>
